import React, { useState } from "react";
import { Modal, Form, Input, Select, InputNumber, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSeletoresDestinatarios } from "../hooks/useSeletoresDestinatarios";
import { useCategoriasMensagem } from "../hooks/useCategoriasMensagem";
import dayjs from "dayjs";
import { DatePicker } from "antd";



const { TextArea } = Input;

interface ModalAdicionarModeloCampanhaProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (dados: any) => Promise<boolean>;
}

export const ModalAdicionarModeloCampanha: React.FC<
  ModalAdicionarModeloCampanhaProps
> = ({ open, onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // ✅ Hook que carrega automaticamente os seletores ativos
  const { seletores, loading: loadingSeletores } = useSeletoresDestinatarios();
  const { categorias, loading: loadingCategorias } = useCategoriasMensagem();


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const prioridade = values.prioridade ?? 10;

      if (values.frequenciaDias <= 0) {
        message.warning("A frequência deve ser maior que zero.");
        return;
      }

      setLoading(true);

      const payload = {
        nome: values.nome,
        descricao: values.descricao,
        seletorId: values.seletorId || null,
        parametroSeletor: values.parametroSeletor
          ? JSON.parse(values.parametroSeletor)
          : null,
        prioridade,
        frequenciaDias: values.frequenciaDias,
        ativo: true,
        categoriaMensagemId: values.categoriaMensagemId,
        proximaExecucao: values.proximaExecucao
          ? dayjs(values.proximaExecucao).toISOString()
          : undefined, // se não informar, o backend usa default(now())
          limiteMensal: values.limiteMensal,
      };

 
      const sucesso = await onSubmit(payload);

      if (sucesso) {
        // message.success("Modelo de campanha criado com sucesso!");
        form.resetFields();
        onClose();
      } else {
        message.error("Erro ao criar modelo de campanha.");
      }
    } catch (error: any) {
      console.error("Erro ao salvar modelo:", error);
      message.error(error?.message || "Erro ao salvar modelo de campanha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Adicionar Modelo de Campanha"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Nome"
          name="nome"
          rules={[{ required: true, message: "Informe o nome do modelo" }]}
        >
          <Input placeholder="Digite o nome do modelo de campanha" />
        </Form.Item>

        <Form.Item
          label="Descrição"
          name="descricao"
          rules={[{ required: true, message: "Informe a descrição" }]}
        >
          <TextArea rows={3} placeholder="Descrição do modelo" />
        </Form.Item>

        {/* 🔹 Select de seletores, agora vindo do hook */}
        <Form.Item
          label="Seletor"
          name="seletorId"
          rules={[{ required: true, message: "Selecione um seletor" }]}
        >
          <Select
            placeholder="Selecione o seletor"
            loading={loadingSeletores}
            allowClear
          >
            {seletores.map((sel) => (
              <Select.Option key={sel.id} value={sel.id}>
                {sel.nome}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Parâmetro do Seletor (JSON)"
          name="parametroSeletor"
          tooltip="Exemplo: { 'excludeEtiquetaIds': [1, 2], 'includeEtiquetaIds': [1, 2] }"
        >
          <TextArea
            rows={3}
            placeholder="Informe parâmetros em formato JSON (opcional)"
          />
        </Form.Item>
        <Form.Item
          label="Categoria da Mensagem"
          name="categoriaMensagemId"
          rules={[{ required: true, message: "Selecione a categoria da mensagem" }]}
        >
          <Select
            placeholder="Selecione a categoria da mensagem"
            loading={loadingCategorias}
            allowClear
          >
            {categorias.map((cat:any) => (
              <Select.Option key={cat.id} value={cat.id}>
                {cat.nome}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>


        <Form.Item
          label="Prioridade (1 a 10)"
          name="prioridade"
          tooltip="1 = mais alta, 10 = mais baixa"
        >
          <InputNumber min={1} max={10} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="Frequência (em dias)"
          name="frequenciaDias"
          rules={[
            { required: true, message: "Informe a frequência em dias" },
            {
              validator(_, value) {
                if (!value || value > 0) return Promise.resolve();
                return Promise.reject(
                  new Error("A frequência deve ser maior que zero")
                );
              },
            },
          ]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
            label="Limite mensal"
            name="limiteMensal"
            rules={[
              { required: true, message: "Informe o limite mensal" },
              {
                validator(_, value) {
                  if (value === undefined || value >= 0) return Promise.resolve();
                  return Promise.reject(new Error("O limite deve ser zero ou maior"));
                },
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

        <Form.Item
          label="Próxima execução"
          name="proximaExecucao"
          tooltip="Se não for informado, será executado na próxima rodada do cron"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve(); // opcional
                if (dayjs(value).isSame(dayjs(), "minute") || dayjs(value).isAfter(dayjs())) {
                    return Promise.resolve();
                }
                return Promise.reject(new Error("A data não pode ser no passado."));

              },
            },
          ]}
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            style={{ width: "100%" }}
            placeholder="Selecione uma data e hora (opcional)"
          />
        </Form.Item>


        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<PlusOutlined />}
            block
          >
            Adicionar Modelo
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
